<script setup lang="ts">
import { faCheck, faMinus } from '@fortawesome/pro-regular-svg-icons';
import { NuxtLink } from '#components';
import type { CheckboxSize } from '#imports';

interface IProps {
    intermediate?: boolean;
    label?: string;
    description?: string;
    disabled?: boolean;
    isRadio?: boolean;
    radioName?: string;
    size?: CheckboxSize;
    boldLabel?: boolean;
    href?: string;
    mobileWrapLabel?: boolean;
    readOnly?: boolean;
    wrapLabel?: boolean;
    loading?: boolean;
}

const {
    label = '',
    description = '',
    disabled,
    isRadio,
    radioName = '',
    size = 'medium',
    boldLabel,
    mobileWrapLabel,
    readOnly,
    wrapLabel,
    loading,
    href = '',
    intermediate,
} = defineProps<IProps>();

const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

const modelValue = defineModel<boolean>({ default: false });

const native = ref<HTMLInputElement>();

const icon = computed(() => {
    if (modelValue.value) {
        if (isRadio) {
            return undefined;
        } else {
            return faCheck;
        }
    } else if (intermediate) {
        if (isRadio) {
            return undefined;
        } else {
            return faMinus;
        }
    } else {
        return undefined;
    }
});

const component = computed(() => {
    if (href) {
        return NuxtLink;
    } else {
        return 'label';
    }
});

watch(
    modelValue,
    value => {
        if (native.value) {
            native.value.checked = value;
            native.value.value = value?.toString();
        }
    },
    { immediate: true }
);
</script>

<template>
    <component
        :is="component"
        :to="href"
        class="mr-checkbox-container"
        :class="{
            disabled,
            radio: isRadio,
            medium: size === 'medium',
            large: size === 'large',
            'bold-label': boldLabel,
            'mobile-wrap-label': mobileWrapLabel,
            'read-only': readOnly,
            'loading-skeleton': loading,
        }"
        :event="disabled || loading ? '' : 'click'"
        @click.stop="emit('click', $event)"
    >
        <input
            id="input-checkbox"
            ref="native"
            class="input"
            :class="{ intermediate }"
            :type="isRadio ? 'radio' : 'checkbox'"
            :value="modelValue"
            :name="radioName"
            :checked="modelValue"
            :disabled="disabled || readOnly || loading"
            @change="
                !disabled &&
                    !readOnly &&
                    !loading &&
                    (modelValue = ($event.target as any)?.checked ?? false)
            "
        />
        <mr-icon
            v-if="icon"
            class="icon"
            :class="{ 'loading-skeleton': loading }"
            size="free"
            :icon
            color="white"
        />
        <span v-else class="icon" />
        <div v-if="label || description" class="info-container">
            <div
                v-if="label"
                class="checkbox-label"
                :class="{ wrap: wrapLabel }"
            >
                <slot name="label" :label>{{ label }}</slot>
            </div>
            <div v-if="description" class="checkbox-description">
                {{ description }}
            </div>
        </div>
    </component>
</template>

<style scoped lang="scss">
.mr-checkbox-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 30px;
    box-sizing: border-box;
    text-decoration: none !important;
    cursor: pointer;

    &.medium {
        @include tg-m($w5);
        > .icon {
            transform: scale(0.88);
        }
    }
    &.large {
        @include tg-l($w5);
    }
    &.bold-label {
        &.medium {
            @include tg-m($w7);
        }
        &.large {
            @include tg-l($w7);
        }
    }

    &:not(.disabled):not(.read-only):hover {
        > .icon {
            background: usePalette($p-blue, $p-100);
        }

        .input:checked,
        .input.intermediate {
            ~ .icon {
                background: usePalette($p-blue, $p-500);
                border-color: usePalette($p-blue, $p-500);
            }
        }
    }

    &.disabled {
        cursor: not-allowed;
        .input:checked,
        .input.intermediate {
            ~ .icon {
                background: usePalette($p-gray) !important;
            }
        }
        .icon {
            background: usePalette($p-gray, $p-300);
            border-color: usePalette($p-gray, $p-300);
        }
        .checkbox-label {
            color: usePalette($p-gray, $p-600);
        }
    }
    &.read-only {
        cursor: default;
    }

    .input:checked,
    .input.intermediate {
        ~ .icon {
            background: usePalette();
        }
    }

    &.radio {
        .icon {
            position: relative;
            border-radius: 100%;
        }
        .input:checked {
            ~ .icon {
                &::before {
                    content: ' ';
                    width: 9px;
                    height: 9px;
                    border: 2px white solid;
                    border-radius: 100%;
                }
            }
        }
    }
}

.icon {
    align-self: flex-start;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 17px;
    width: 17px;
    background: white;
    border: 2px solid usePalette();
    border-radius: 4px;
    margin: auto 7px;
    box-sizing: border-box;
}

.info-container {
    flex-grow: 1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    .checkbox-description {
        @include tg-m($w5, usePalette($p-cool-gray, $p-800));
    }
}

.checkbox-label {
    margin-right: 4px;
    color: usePalette();
    white-space: nowrap;
    &.wrap {
        white-space: unset;
    }
}

input {
    display: none;
}

@media (max-width: $app-mobile-max-width) {
    .mr-checkbox-container {
        &.mobile-wrap-label {
            align-items: flex-start;
            .checkbox-label {
                white-space: unset;
            }
        }
    }
}
</style>
